import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getVehicleCompliance } from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /compliance/vehicles — fleet vehicle compliance master list.
 *
 * For each active vehicle, shows insurance / registration / inspection
 * expiry. Gaps (missing or expired) flagged with red chips.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const Kpi = ({ label, value, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, minWidth: 0 }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent || "#0f172a" }}>{value}</Typography>
    </CardContent>
  </Card>
);

const expiryColor = (ms) => {
  if (!ms) return "#94a3b8";
  const days = Math.floor((ms - Date.now()) / DAY_MS);
  if (days < 0) return "#dc2626";
  if (days <= 30) return "#f97316";
  return "#16a34a";
};
const expiryLabel = (ms) => {
  if (!ms) return "—";
  const days = Math.floor((ms - Date.now()) / DAY_MS);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "today";
  return `${days}d`;
};

export const VehicleCompliancePage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | compliant | gaps

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getVehicleCompliance();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load vehicle compliance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { total: rows.length, compliant: 0, gaps: 0, expiring: 0 };
    rows.forEach((r) => {
      if (r.fullyCompliant) c.compliant++;
      else c.gaps++;
      if (
        r.nextExpiryAt &&
        r.nextExpiryAt - Date.now() < 30 * DAY_MS &&
        r.nextExpiryAt - Date.now() >= 0
      ) {
        c.expiring++;
      }
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "compliant" && !r.fullyCompliant) return false;
      if (filter === "gaps" && r.fullyCompliant) return false;
      if (!q) return true;
      const hay = `${r.make || ""} ${r.model || ""} ${r.label || ""} ${r.licensePlate || ""} ${r.driverFullName || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, filter]);

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Fleet Vehicle Compliance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Insurance, registration, and inspection expiry for every vehicle in the fleet. Inspection pulls from the assigned driver's most-recent verified VEHICLE_INSPECTION document.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Kpi label="Vehicles" value={counts.total} />
        <Kpi label="Fully compliant" value={counts.compliant} accent="#16a34a" />
        <Kpi label="With gaps" value={counts.gaps} accent={counts.gaps > 0 ? "#dc2626" : "#0f172a"} />
        <Kpi label="Expiring (30d)" value={counts.expiring} accent={counts.expiring > 0 ? "#f97316" : "#0f172a"} />
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search make / model / plate / driver"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        {[
          { k: "all", label: "All" },
          { k: "compliant", label: "Compliant" },
          { k: "gaps", label: "Gaps" },
        ].map((b) => (
          <Chip
            key={b.k}
            size="small"
            label={b.label}
            onClick={() => setFilter(b.k)}
            sx={{
              backgroundColor: filter === b.k ? "#1F4E78" : "#f1f5f9",
              color: filter === b.k ? "#ffffff" : "#475569",
              fontWeight: filter === b.k ? 700 : 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f8fafc" } }}>
              <TableCell>Vehicle</TableCell>
              <TableCell>Plate</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell align="center">Insurance</TableCell>
              <TableCell align="center">Registration</TableCell>
              <TableCell align="center">Inspection</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  {rows.length === 0
                    ? "No vehicles in fleet yet."
                    : "No vehicles match the current filter."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => {
                const cell = (label, ts, missing, expired) => {
                  if (missing) {
                    return (
                      <Chip
                        size="small"
                        label="Missing"
                        sx={{ height: 20, fontSize: 11, backgroundColor: "#fee2e2", color: "#991b1b", fontWeight: 600 }}
                      />
                    );
                  }
                  return (
                    <Tooltip title={ts ? new Date(ts).toLocaleDateString() : ""}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: expired ? "#dc2626" : expiryColor(ts) }}
                      >
                        {expiryLabel(ts)}
                      </Typography>
                    </Tooltip>
                  );
                };
                return (
                  <TableRow key={r.vehicleId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {[r.year, r.make, r.model].filter(Boolean).join(" ")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.label || r.vehicleType || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.licensePlate || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.driverFullName || "—"}</Typography>
                      {r.driverPhone ? (
                        <Typography variant="caption" color="text.secondary">{r.driverPhone}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      {cell("Insurance", r.insuranceExpiresAt, r.missingInsurance, r.insuranceExpired)}
                    </TableCell>
                    <TableCell align="center">
                      {cell("Registration", r.registrationExpiresAt, r.missingRegistration, r.registrationExpired)}
                    </TableCell>
                    <TableCell align="center">
                      {cell("Inspection", r.inspectionExpiresAt, r.missingInspection, r.inspectionExpired)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={r.fullyCompliant ? "Compliant" : "Gaps"}
                        sx={{
                          backgroundColor: r.fullyCompliant ? "#dcfce7" : "#fee2e2",
                          color: r.fullyCompliant ? "#166534" : "#991b1b",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {r.driverDetailsId ? (
                        <Button
                          size="small"
                          onClick={() => navigate(`/compliance/drivers/${r.driverDetailsId}`)}
                          endIcon={<OpenInNewIcon fontSize="small" />}
                        >
                          Driver
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default VehicleCompliancePage;
